/**
 * `useChartWidth` — how wide a chart's box really is, in CSS pixels (client, 7 Oct 2026:
 * "the charts in the analysis side seem rigid/fixed and not interactive and responsive").
 *
 * Until this round every chart drew on a fixed viewBox (394, 320, 360 …) and let CSS stretch
 * it, so a wider card printed bigger letters and a narrower one smaller letters, and nothing
 * reflowed. A chart that knows its width lays itself out in real pixels at that width
 * instead: text stays the size it was designed at, and the plot takes the room.
 *
 * The width comes from a `ResizeObserver` on the element `ref` points at (or on its parent,
 * for a chart whose own box is sized by its content, like the donut), read once
 * synchronously before the first paint and then on every resize, debounced to one update per
 * animation frame so a dragged panel does not re-lay a chart on every pixel. Where nothing
 * can measure (jsdom, a server render, a box not laid out yet) it answers `fallback`, which
 * is the width the chart always had, so a test sees the chart exactly as before.
 *
 * `zoom` divides the measured width: the lightbox draws its copy of a chart larger than one
 * CSS pixel per unit (`ChartZoomContext`), so a chart laid out at `measured / zoom` and drawn
 * at `measured` prints its text `zoom` times larger.
 */
import { createContext, useContext, useLayoutEffect, useState, type RefObject } from 'react';

/** The scale the surrounding container draws charts at: 1 inline, `LIGHTBOX_ZOOM` expanded. */
export const ChartZoomContext = createContext(1);

/** The zoom a chart is drawn at here (1 on the page, more in the lightbox). */
export function useChartZoom(): number {
  return useContext(ChartZoomContext);
}

/** The content-box width of `el`, or 0 where it has no layout. */
export function contentWidth(el: Element): number {
  const rect = el.getBoundingClientRect();
  let width = rect.width;
  if (!(width > 0)) return 0;
  if (typeof window !== 'undefined' && typeof window.getComputedStyle === 'function') {
    const cs = window.getComputedStyle(el);
    const px = (v: string) => (Number.parseFloat(v) || 0);
    width -=
      px(cs.paddingLeft) + px(cs.paddingRight) + px(cs.borderLeftWidth) + px(cs.borderRightWidth);
  }
  return Math.max(0, width);
}

export interface ChartWidthOptions {
  /** Divide the measured width by this (the lightbox's zoom). Default 1. */
  zoom?: number;
  /** Measure `ref`'s parent rather than `ref` itself. */
  parent?: boolean;
}

/**
 * The width of the box `ref` points at, in CSS pixels (divided by `zoom`), rounded to whole
 * pixels; `fallback` until, or wherever, it cannot be measured.
 */
export function useChartWidth(
  ref: RefObject<Element | null>,
  fallback: number,
  { zoom = 1, parent = false }: ChartWidthOptions = {},
): number {
  const [measured, setMeasured] = useState(0);

  useLayoutEffect(() => {
    const node = ref.current;
    const target = parent ? node?.parentElement : node;
    if (!target) return undefined;
    const apply = (width: number) => {
      const next = Math.round(width);
      // Sub-pixel jitter (a scrollbar's fractional width, a transform) must not re-lay a chart.
      setMeasured((prev) => (next > 0 && Math.abs(next - prev) >= 1 ? next : prev));
    };
    apply(contentWidth(target));
    if (typeof ResizeObserver === 'undefined') return undefined;
    let frame = 0;
    let latest = 0;
    const raf =
      typeof requestAnimationFrame === 'function'
        ? requestAnimationFrame
        : (cb: FrameRequestCallback) => setTimeout(() => cb(0), 16) as unknown as number;
    const caf =
      typeof cancelAnimationFrame === 'function'
        ? cancelAnimationFrame
        : (id: number) => clearTimeout(id);
    const observer = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (!entry) return;
      latest = entry.contentRect.width;
      if (frame) return;
      frame = -1;
      const id = raf(() => {
        frame = 0;
        apply(latest);
      });
      // A scheduler that ran the callback at once has already cleared the slot.
      if (frame === -1) frame = id;
    });
    observer.observe(target);
    return () => {
      observer.disconnect();
      if (frame > 0) caf(frame);
    };
  }, [ref, parent]);

  if (!(measured > 0)) return fallback;
  const z = zoom > 0 ? zoom : 1;
  return Math.round(measured / z);
}

export default useChartWidth;
